import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Settings, Loader2, Image as ImageIcon, Upload, Trash2, Plus } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { termsToHtml } from "../../../lib/terms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoV, setLogoV] = useState(Date.now());
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/quotes/settings`); setForm(r.data.settings); }
    catch (e) { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/quotes/settings`, {
        company_name: form.company_name, company_details: form.company_details,
        logo_url: form.logo_url, footer_note: form.footer_note,
        default_terms: form.default_terms, default_valid_days: Number(form.default_valid_days) || 30,
        terms_templates: (form.terms_templates || []).filter((t) => (t.name || "").trim() || (t.body || "").trim()),
      });
      toast.success("Quote settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/quotes/settings/logo`, fd);
      setForm(r.data.settings);
      setLogoV(Date.now());
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try { const r = await axios.delete(`${API}/quotes/settings/logo`); setForm(r.data.settings); toast.success("Logo removed"); }
    catch (err) { toast.error("Failed to remove logo"); }
  };

  const addTerms = () => setForm((f) => ({ ...f, terms_templates: [...(f.terms_templates || []), { id: `t_${Date.now()}`, name: "", body: "" }] }));
  const updateTerms = (i, patch) => setForm((f) => ({ ...f, terms_templates: (f.terms_templates || []).map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  const removeTerms = (i) => setForm((f) => ({ ...f, terms_templates: (f.terms_templates || []).filter((_, idx) => idx !== i) }));

  if (loading || !form) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#8A6D00]" /></div>;

  return (
    <div className="space-y-4" data-testid="quote-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center"><Settings className="w-5 h-5 text-[#8A6D00]" /></div>
        <div>
          <h2 className="heading text-2xl text-[#1A1A1A] tracking-tight">Quote Settings</h2>
          <p className="text-sm text-[#59554D]">Company details, logo, default terms and validity that appear on every quote and PDF.</p>
        </div>
      </div>

      <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl">
        <CardContent className="p-5 space-y-4 max-w-2xl">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs text-[#59554D]">Company name</label><Input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="qs-company-name" /></div>
            <div><label className="text-xs text-[#59554D]">Default validity (days)</label><Input type="number" value={form.default_valid_days || 30} onChange={(e) => setForm({ ...form, default_valid_days: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="qs-valid-days" /></div>
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Logo <span className="text-[#8A857A]">(shown on the quote & PDF)</span></label>
            <div className="mt-1.5 flex items-start gap-3 flex-wrap">
              <div className="p-3 rounded-lg border border-[#E6E2D9] bg-[#FBFAF7] min-w-[140px] min-h-[64px] flex items-center justify-center" data-testid="qs-logo-preview">
                {form.has_logo ? (
                  <img src={`${API}/quotes/settings/logo?v=${logoV}`} alt="Logo" className="max-h-14" />
                ) : form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="max-h-14" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="text-xs text-[#8A857A] inline-flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> No logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="qs-logo-file" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="qs-logo-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload logo
                </Button>
                {form.has_logo && <Button type="button" variant="ghost" onClick={removeLogo} className="text-[#C23A00] hover:bg-[#FFE9E0]" data-testid="qs-logo-remove"><Trash2 className="w-4 h-4 mr-1.5" /> Remove</Button>}
              </div>
            </div>
            <label className="text-xs text-[#59554D] block mt-3">…or paste a logo URL <span className="text-[#8A857A]">(used only if no file uploaded)</span></label>
            <Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="qs-logo-url" />
          </div>
          <div><label className="text-xs text-[#59554D]">Company details <span className="text-[#8A857A]">(address, ABN, phone — shown under the logo)</span></label><Textarea rows={3} value={form.company_details || ""} onChange={(e) => setForm({ ...form, company_details: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="qs-company-details" /></div>
          <div>
            <label className="text-xs text-[#59554D]">Default Terms &amp; Conditions <span className="text-[#8A857A]">(pre-selected on new quotes)</span></label>
            <Textarea rows={8} value={form.default_terms || ""} onChange={(e) => setForm({ ...form, default_terms: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] font-mono text-[12.5px]" data-testid="qs-default-terms" />
            <div className="text-[11px] text-[#8A857A] mt-1">
              Formatting: <b>#&nbsp;Heading</b>, <b>##&nbsp;Sub-heading</b>, <b>**bold**</b>, <b>-&nbsp;bullet</b>. Leave a blank line between paragraphs.
            </div>
            {(form.default_terms || "").trim() && (
              <div className="mt-2 rounded-lg border border-[#DAD5CA] bg-white p-3 max-h-64 overflow-y-auto qs-terms-preview" data-testid="qs-terms-preview">
                <div className="text-[10px] uppercase tracking-[0.09em] text-[#8A857A] mb-1.5">Preview</div>
                <style>{`.qs-terms-preview h3{font-family:Manrope,sans-serif;font-size:14px;font-weight:800;color:#1A1A1A;margin:12px 0 4px}.qs-terms-preview h4{font-size:12.5px;font-weight:700;color:#1A1A1A;margin:10px 0 3px}.qs-terms-preview p{font-size:12.5px;line-height:1.6;color:#59554D;margin:0 0 8px}.qs-terms-preview ul{margin:0 0 8px;padding-left:18px}.qs-terms-preview li{font-size:12.5px;line-height:1.5;color:#59554D}.qs-terms-preview strong{color:#1A1A1A}`}</style>
                <div dangerouslySetInnerHTML={{ __html: termsToHtml(form.default_terms) }} />
              </div>
            )}
          </div>

          {/* Other T&C templates */}
          <div className="rounded-lg border border-[#E6E2D9] bg-[#FBFAF7] p-3.5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[13px] font-semibold text-[#1A1A1A]">Other Terms &amp; Conditions</div>
                <div className="text-[11px] text-[#8A857A]">Add alternative sets (e.g. Enterprise, Hardware, Trial). Selectable when building a quote.</div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addTerms} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED] shrink-0" data-testid="qs-terms-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add set</Button>
            </div>
            {(form.terms_templates || []).length === 0 ? (
              <p className="text-[12px] text-[#A39D8F] py-2">No other sets yet — just the Default above.</p>
            ) : (
              <div className="space-y-3">
                {(form.terms_templates || []).map((t, i) => (
                  <div key={t.id || i} className="rounded-md border border-[#E6E2D9] bg-white p-2.5" data-testid={`qs-terms-item-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Input value={t.name || ""} onChange={(e) => updateTerms(i, { name: e.target.value })} placeholder="Name (e.g. Enterprise)" className="bg-[#F4F2ED] border-[#DAD5CA] h-8 text-sm" data-testid={`qs-terms-name-${i}`} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTerms(i)} className="text-[#C23A00] hover:bg-[#FFE9E0] shrink-0 h-8 px-2" data-testid={`qs-terms-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    <Textarea rows={3} value={t.body || ""} onChange={(e) => updateTerms(i, { body: e.target.value })} placeholder="Terms & conditions text…" className="bg-[#F4F2ED] border-[#DAD5CA] text-sm" data-testid={`qs-terms-body-${i}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div><label className="text-xs text-[#59554D]">Footer note <span className="text-[#8A857A]">(bottom of the PDF)</span></label><Textarea rows={2} value={form.footer_note || ""} onChange={(e) => setForm({ ...form, footer_note: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="qs-footer" /></div>
          <div className="flex justify-end"><Button onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="qs-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Save settings</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}
